import React, { useState, useEffect, useCallback, useRef } from 'react';
import './NotificationsPill.css';

const NotificationsPill = ({ apiUrl, adminToken, onError }) => {
  // Do NOT hydrate from localStorage on mount so the pill never shows stale total.
  // We always fetch with refresh=true on load; displaying cached first caused "pill stuck at 15" etc.
  const [tally, setTally] = useState(null);
  
  // Use refs to access current values without adding them as callback dependencies
  // This prevents the infinite loop caused by inline onError functions
  const tallyRef = useRef(tally);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    tallyRef.current = tally;
  }, [tally]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const fetchNotifications = useCallback(async (silent = false, forceRefresh = false) => {
    if (!adminToken) {
      return;
    }
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      // Refresh from Bluesky when: forced (e.g. initial load or manual), or last response >1 min old
      const currentTally = tallyRef.current;
      const cacheAge = currentTally ? (Date.now() - (currentTally.timestamp ? new Date(currentTally.timestamp).getTime() : 0)) : Infinity;
      const needsRefresh = forceRefresh || cacheAge > 1 * 60 * 1000; // 1 minute so pill syncs soon after zeroing on BS
      const url = `${apiUrl}/api/notifications/tally?refresh=${needsRefresh}`;
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/6c1dc97d-7636-48a1-91eb-cd91b10f472a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'NotificationsPill.js:fetch',message:'tally request',data:{forceRefresh,needsRefresh,cacheAgeMs:cacheAge,url:url.replace(/[?].*$/,'?...')},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1'})}).catch(()=>{});
      // #endregion
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (response.status === 401 || response.status === 403) {
        onErrorRef.current?.('Session expired');
        return;
      }

      const data = await response.json();
      // #region agent log
      const _tu = data.total_unread != null ? data.total_unread : -1;
      const _rf = data.refresh;
      const _pc = data.tally ? Object.fromEntries(Object.keys(data.tally).map(function(k) { return [k, (data.tally[k] && data.tally[k].alert_count) != null ? data.tally[k].alert_count : null]; })) : {};
      fetch('http://127.0.0.1:7243/ingest/6c1dc97d-7636-48a1-91eb-cd91b10f472a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'NotificationsPill.js:response',message:'tally response',data:{totalUnread:_tu,refresh:_rf,timestamp:data.timestamp,perChar:_pc},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H2'})}).catch(function(){});
      // #endregion
      setTally(data);
      
      // Cache the result (backend caches for 1 minute, frontend caches for 2 minutes)
      try {
        localStorage.setItem('notifications_cache', JSON.stringify({
          tally: data,
          timestamp: Date.now()
        }));
      } catch (e) {
        // Ignore cache errors
      }
    } catch (err) {
      if (!silent) {
        console.error('Notifications fetch error:', err);
      }
    }
  }, [adminToken, apiUrl]); // Removed onError - using ref instead

  // Initial fetch on mount: always force refresh so pill shows actual Bluesky state when you open the desk
  const initialFetchDone = useRef(false);
  useEffect(() => {
    if (adminToken && !initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchNotifications(false, true); // Force refresh on load so pill recalibrates immediately
    }
  }, [adminToken, fetchNotifications]);
  
  // Background refresh every 1 min; always force refresh so pill syncs soon after user zeros on Bluesky
  useEffect(() => {
    if (!adminToken) return;
    
    const interval = setInterval(() => {
      fetchNotifications(true, true); // Silent; always force Bluesky re-fetch so pill doesn't stay out of sync
    }, 1 * 60 * 1000); // 1 minute
    
    return () => clearInterval(interval);
  }, [adminToken, fetchNotifications]);

  // If no token, show placeholder
  if (!adminToken) {
    return (
      <div className="notifications-pill loading">
        <span title="Not logged in">—</span>
      </div>
    );
  }

  if (!tally) {
    return (
      <div className="notifications-pill loading">
        <span></span>
      </div>
    );
  }

  const accounts = tally.tally || {};
  const totalUnread = tally.total_unread || 0;
  
  // Always show all 6 accounts in consistent order (including zeros)
  const expectedAccounts = ['bitbard', 'lady_macbeth', 'iago', 'sir_andrew', 'falstaff', 'miranda'];
  
  // Build display string with abbreviated character names
  const displayItems = expectedAccounts
    .map(char => {
      const data = accounts[char] || { alert_count: 0 };
      const shortName = char === 'bitbard' ? 'BB'
                       : char === 'lady_macbeth' ? 'LM'
                       : char === 'sir_andrew' ? 'A'
                       : char === 'iago' ? 'I'
                       : char === 'miranda' ? 'M'
                       : char === 'falstaff' ? 'F'
                       : char;
      return `${shortName}:${data.alert_count || 0}`;
    })
    .join(' | ');

  return (
    <div className="notifications-pill">
      <button 
        className="notifications-button"
        type="button"
        title={`Unread: BB=${accounts.bitbard?.alert_count || 0} | LM=${accounts.lady_macbeth?.alert_count || 0} | I=${accounts.iago?.alert_count || 0} | A=${accounts.sir_andrew?.alert_count || 0} | F=${accounts.falstaff?.alert_count || 0} | M=${accounts.miranda?.alert_count || 0}. Click to refresh from Bluesky.`}
        onClick={() => fetchNotifications(false, true)}
      >
        <span className="tally-text">{displayItems}</span>
        {totalUnread > 0 && <span className="badge">{totalUnread}</span>}
      </button>
    </div>
  );
};

export default NotificationsPill;

